<template lang="pug">
.robots
  header.robots__hero
    .robots__hero-grid
    .robots__hero-glow.robots__hero-glow--blue
    .robots__hero-glow.robots__hero-glow--red
    .robots__hero-inner
      p.robots__eyebrow
        span.robots__eyebrow-pip
        | Participantes
      h1.robots__title Robôs Participantes
      p.robots__sub Explore os robôs inscritos por categoria e acompanhe o status de cada um na competição.
      .robots__hero-meta
        .robots__chip(v-if="getCurrentEvent")
          span.robots__chip-label Evento
          span.robots__chip-value {{ getCurrentEvent.name }}
        .robots__chip
          span.robots__chip-label Robôs
          span.robots__chip-value {{ totalRobots }}
        .robots__chip
          span.robots__chip-label Aprovados
          span.robots__chip-value {{ approvedRobots.length }}
        button.robots__export(
          v-if="isInspector"
          type="button"
          :disabled="!approvedRobots.length"
          :title="approvedRobots.length ? 'Baixa um CSV com os robôs aprovados nas duas inspeções' : 'Nenhum robô aprovado nas duas inspeções ainda'"
          @click="doExportApproved"
        )
          FontAwesomeIcon(:icon="['fas', 'file-download']")
          | Exportar aprovados

  .robots__wrap
    .robots__empty(v-if="!totalRobots")
      span.robots__empty-ic 🤖
      h3.robots__empty-title Nenhum robô inscrito
      p.robots__empty-text Ainda não há robôs participantes neste evento.

    template(v-else)
      section.robots__section(
        v-for="(category, indexCategories) in takeCategories"
        :key="category.value"
      )
        button.robots__cat-head(
          @click="doShowCategory(indexCategories)"
          :class="{ 'robots__cat-head--open': isOpen(indexCategories) }"
        )
          .robots__cat-left
            h2.robots__cat-name {{ category.name }}
            span.robots__cat-count {{ takeRobots[indexCategories].length }}
          FontAwesomeIcon.robots__cat-chevron(
            :class="isOpen(indexCategories) ? 'robots__cat-chevron--open' : ''"
            :icon="['fas', 'chevron-down']"
          )

        .robots__grid(v-if="isOpen(indexCategories)")
          p.robots__cat-empty(v-if="!takeRobots[indexCategories].length") Nenhum robô nesta categoria.
          .robots__card(
            v-for="(robot, indexRobot) in takeRobots[indexCategories]"
            :key="indexRobot"
            :class="{ 'robots__card--clickable': isInspector }"
            @click="isInspector ? doNavigateToInspectRobot(category.value, indexRobot) : null"
          )
            .robots__card-media
              //- A lista /get_robots nao devolve imagem (o JSON iria a vários MB
              //- com 200+ robôs), por isso o card ficava sempre no placeholder.
              //- A miniatura vem por robô, no tamanho em que é exibida, com
              //- lazy-load e cache do navegador. Sem foto o endpoint dá 404 e o
              //- @error troca de volta para o ícone.
              img.robots__card-img(
                v-if="!robot.semFoto"
                :src="thumbUrl(robot.id)"
                :alt="robot.name"
                loading="lazy"
                decoding="async"
                @error="doSemFoto(robot)"
              )
              FontAwesomeIcon.robots__card-ph(v-else :icon="['fas', 'robot']")
              span.robots__status(:style="statusStyle(robot.status)")
                span.robots__status-dot(:style="{ background: robot.status.color }")
                | {{ robot.status.label }}
            .robots__card-body
              h3.robots__card-name {{ robot.name }}
              p.robots__card-team
                FontAwesomeIcon.robots__card-team-ic(:icon="['fas', 'users']")
                | {{ robot.team }}
              template(v-if="isInspector")
                .robots__insp
                  span.robots__insp-chip(:class="robot.dynamic ? 'is-ok' : 'is-no'")
                    FontAwesomeIcon(:icon="['fas', robot.dynamic ? 'check' : 'times']")
                    | Dinâmica
                  span.robots__insp-chip(:class="robot.static ? 'is-ok' : 'is-no'")
                    FontAwesomeIcon(:icon="['fas', robot.static ? 'check' : 'times']")
                    | Estática
                span.robots__insp-btn
                  FontAwesomeIcon(:icon="['fas', 'clipboard-check']")
                  | Inspecionar

</template>

<script>
import {
  mapGetters,
  mapActions
} from 'vuex'

// Sentinela de `fights.row` para luta da fase de grupos (espelha groupFightRow no
// backend). Não faz parte da árvore, então não é Winners nem Losers.
const GROUP_ROW = -1000

export default {
  name: 'Robots',
  props: {},
  data: () => ({
    // índices explicitamente colapsados; undefined = aberto (padrão)
    showCategories: [],
    // ids cuja miniatura deu 404: caem no ícone de placeholder
    semFoto: []
  }),
  created () {
    // force: sempre recarrega ao entrar na tela (a lista pode ter mudado por
    // outro operador (inscricao/inspecao/luta) e o cache local nao saberia)
    this.setEvents().then(() => {
      this.setRobots({ event_id: this.getCurrentEventId, force: true })
    })
    this.setCategories()
  },
  computed: {
    ...mapGetters('robots', [
      'getRobots'
    ]),
    ...mapGetters('categories', [
      'getCategories'
    ]),
    ...mapGetters('users', [
      'getUser'
    ]),
    ...mapGetters('events', [
      'getCurrentEventId',
      'getCurrentEvent'
    ]),
    // Inspetor (2) — e tambem Admin (1) / Organizador (6), que acumulam todas as
    // funcoes. Mesma regra do backend (inspection_* aceita 2, 1 ou 6).
    isInspector () {
      const roles = this.getUser.roles
      return Array.isArray(roles) && (roles.includes(2) || roles.includes(1) || roles.includes(6))
    },
    takeRobots () {
      return this.takeCategories.map(category => {
        return this.getRobots
          .filter(robot => robot.category_id === category.value)
          .map(robot => ({
            id: robot.id,
            name: robot.name,
            team: robot.team_name,
            status: this.computeRobotStatus(robot),
            dynamic: robot.dynamic_inspection,
            static: robot.static_inspection,
            // has_image vem da lista (booleano, sem a foto): evita pedir a
            // miniatura de quem não tem foto — numa lista de 222 robôs sem foto
            // eram 222 requisições que davam 404. O semFoto cobre o resto
            // (foto ilegível, miniatura que falhou).
            semFoto: !robot.has_image || this.semFoto.includes(robot.id)
          }))
      })
    },
    takeCategories () {
      let ret = []
      if (this.getCategories.length) {
        ret = [...this.getCategories].map(category => {
          return { name: category.name, value: category.id }
        })
      }
      return ret
    },
    totalRobots () {
      return this.getRobots ? this.getRobots.length : 0
    },
    // Aprovado = elegível à chave: as DUAS inspeções passaram (mesma regra do
    // backend em generate_brackets).
    approvedRobots () {
      return (this.getRobots || []).filter(robot => robot.static_inspection && robot.dynamic_inspection)
    }
  },
  methods: {
    ...mapActions('robots', [
      'setRobots',
      'setCurrentRobot'
    ]),
    ...mapActions('categories', [
      'setCategories'
    ]),
    ...mapActions('events', [
      'setEvents'
    ]),
    isOpen (index) {
      return this.showCategories[index] !== false
    },
    doNavigateToInspectRobot (category, index) {
      this.setCurrentRobot(this.getRobots.filter(robot => robot.category_id === category)[index])
      this.$router.push('InspectRobot')
    },
    doShowCategory (e) {
      const arr = [...this.showCategories]
      arr[e] = !this.isOpen(e)
      this.showCategories = arr
    },
    // Miniatura servida pela API como imagem de verdade (não base64 no JSON),
    // então o navegador cacheia e o loading="lazy" só busca o que aparece.
    thumbUrl (robotId) {
      return '/api/robot_thumb?robot_id=' + robotId
    },
    doSemFoto (robot) {
      if (!this.semFoto.includes(robot.id)) this.semFoto = [...this.semFoto, robot.id]
    },
    categoryName (categoryId) {
      const cat = this.getCategories.find(c => c.id === categoryId)
      return cat ? cat.name : ''
    },
    // Exporta os aprovados em CSV. Separador ";" e BOM UTF-8 porque é o que o
    // Excel em pt-BR abre direito: com "," ele joga tudo numa coluna só, e sem
    // BOM os acentos viram lixo. O Sheets detecta o ";" no import.
    doExportApproved () {
      const escape = (valor) => {
        const s = String(valor === null || valor === undefined ? '' : valor).trim()
        return /[";\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const linhas = [['Robô', 'Categoria', 'Equipe']]
      const ordenados = [...this.approvedRobots].sort((a, b) => {
        const porCategoria = this.categoryName(a.category_id).localeCompare(this.categoryName(b.category_id), 'pt-BR')
        return porCategoria || String(a.name).localeCompare(String(b.name), 'pt-BR')
      })
      for (const robot of ordenados) {
        linhas.push([robot.name, this.categoryName(robot.category_id), robot.team_name || 'Sem equipe'])
      }
      const csv = '﻿' + linhas.map(l => l.map(escape).join(';')).join('\r\n')

      const evento = (this.getCurrentEvent && this.getCurrentEvent.name) || 'evento'
      const slug = evento.normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      const hoje = new Date().toISOString().slice(0, 10)

      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `robos-aprovados-${slug}-${hoje}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    statusStyle (status) {
      // hex de 6 dígitos + alpha em hex (RRGGBBAA) => tinta clara p/ o pill
      return {
        color: status.color,
        background: status.color + '18',
        borderColor: status.color + '40'
      }
    },
    computeRobotStatus (robot) {
      const row = robot.fight_row
      if (row === null || row === undefined) {
        return { label: 'Aguardando', color: '#9E9E9E' }
      }
      const ended = robot.fight_ended_at !== null
      // Fase de grupos: perder uma luta de grupo não elimina ninguém, e o robô não
      // está em nenhum lado da chave. Entre uma luta e outra fica "Aguardando".
      if (row === GROUP_ROW) {
        return ended
          ? { label: 'Aguardando', color: '#9E9E9E' }
          : { label: 'Fase de grupos', color: '#00838F' }
      }
      if (!ended) {
        if (row === 999) return { label: 'Grande Final', color: '#F9A825' }
        if (row === 998) return { label: 'Reset do Bracket', color: '#F9A825' }
        if (row >= 0) return { label: `Winners Rd.${row + 1}`, color: '#1565C0' }
        return { label: `Losers Rd.${Math.abs(row)}`, color: '#E65100' }
      }
      if (robot.fight_winner_id === robot.id) {
        if (row === 999 || row === 998) return { label: 'Campeao', color: '#2E7D32' }
        return { label: 'Aguardando', color: '#9E9E9E' }
      }
      if (robot.fight_loser_id === robot.id) {
        if (row === 999 || row === 998) return { label: 'Finalista', color: '#6A1B9A' }
        if (row < 0) return { label: 'Eliminado', color: '#C62828' }
      }
      return { label: 'Aguardando', color: '#9E9E9E' }
    }
  },
  watch: {
    getCurrentEventId (newId) {
      if (newId) this.setRobots({ event_id: newId, force: true })
    }
  }
}
</script>

<style lang="sass" scoped>
  $blue: #287AED
  $blue-dark: #0F4FA8
  $red: #DD3434
  $green: #2E9E4F
  $ink: #0B0E14
  $muted: #5C6673
  $line: rgba(11, 14, 20, 0.10)
  $paper: #FFFFFF
  $page: #F2F4F8
  $display: 'Saira Condensed', 'Lato', sans-serif
  $mono: 'Space Mono', 'Lato', monospace

  .robots
    color: $ink
    background: $page
    min-height: calc(100vh - 64px)

    *
      box-sizing: border-box

    // ---------- HERO ----------
    &__hero
      position: relative
      overflow: hidden
      isolation: isolate
      padding: 30px 0 40px
      background: radial-gradient(circle at 16% 8%, #15233b 0%, $ink 52%, #05070b 100%)
      color: #fff

    &__hero-grid
      position: absolute
      inset: 0
      background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      background-size: 42px 42px
      mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      -webkit-mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      z-index: -2

    &__hero-glow
      position: absolute
      width: 360px
      height: 360px
      border-radius: 50%
      filter: blur(110px)
      opacity: 0.5
      z-index: -1
      &--blue
        background: $blue
        top: -170px
        left: -90px
      &--red
        background: $red
        bottom: -190px
        right: -70px

    &__hero-inner
      position: relative
      max-width: 1240px
      margin: 0 auto
      padding: 0 24px
      > *
        animation: robots-rise .55s cubic-bezier(.2, .7, .2, 1) both
      .robots__title
        animation-delay: .06s
      .robots__sub
        animation-delay: .12s
      .robots__hero-meta
        animation-delay: .18s

    &__eyebrow
      display: inline-flex
      align-items: center
      gap: 10px
      font-family: $mono
      text-transform: uppercase
      letter-spacing: 0.22em
      font-size: 12px
      font-weight: 700
      color: rgba(255, 255, 255, 0.7)
      margin: 0 0 12px

    &__eyebrow-pip
      width: 8px
      height: 8px
      border-radius: 50%
      background: linear-gradient(135deg, #5AA0FF, $red)
      box-shadow: 0 0 0 4px rgba(90, 160, 255, 0.15)

    &__title
      font-family: $display
      font-size: 44px
      line-height: 1.02
      font-weight: 800
      letter-spacing: -0.01em
      margin: 0

    &__sub
      max-width: 560px
      margin: 12px 0 0
      font-size: 15px
      line-height: 1.5
      color: rgba(255, 255, 255, 0.72)

    &__hero-meta
      display: flex
      flex-wrap: wrap
      gap: 12px
      margin-top: 20px

    &__chip
      display: inline-flex
      align-items: center
      gap: 10px
      padding: 8px 16px
      border-radius: 999px
      background: rgba(255, 255, 255, 0.07)
      border: 1px solid rgba(255, 255, 255, 0.14)

    &__chip-label
      font-family: $mono
      font-size: 10px
      text-transform: uppercase
      letter-spacing: 0.14em
      color: rgba(255, 255, 255, 0.55)

    &__chip-value
      font-family: $display
      font-size: 16px
      font-weight: 700
      color: #fff

    &__export
      display: inline-flex
      align-items: center
      gap: 9px
      padding: 8px 18px
      border-radius: 999px
      border: 1px solid rgba(255, 255, 255, 0.22)
      background: rgba(255, 255, 255, 0.10)
      color: #fff
      font-family: $display
      font-size: 15px
      font-weight: 700
      cursor: pointer
      transition: background .18s ease, border-color .18s ease, transform .18s ease
      &:hover:not(:disabled)
        background: rgba(255, 255, 255, 0.2)
        border-color: rgba(255, 255, 255, 0.4)
        transform: translateY(-1px)
      &:disabled
        opacity: 0.45
        cursor: not-allowed

    // ---------- CORPO ----------
    &__wrap
      max-width: 1240px
      margin: 0 auto
      padding: 32px 24px 64px

    &__section
      margin-bottom: 28px

    // ---------- CABEÇALHO DE CATEGORIA (accordion) ----------
    &__cat-head
      width: 100%
      display: flex
      align-items: center
      justify-content: space-between
      gap: 16px
      background: $paper
      border: 1px solid $line
      border-radius: 12px
      padding: 14px 20px
      margin-bottom: 18px
      cursor: pointer
      font: inherit
      text-align: left
      transition: border-color .16s ease, box-shadow .16s ease, transform .16s ease
      &:hover
        border-color: rgba(40, 122, 237, 0.45)
        box-shadow: 0 4px 14px rgba(11, 14, 20, 0.08)
      &--open
        border-color: rgba(40, 122, 237, 0.35)
        box-shadow: 0 4px 14px rgba(11, 14, 20, 0.06)

    &__cat-left
      display: flex
      align-items: center
      gap: 12px

    &__cat-name
      font-family: $display
      font-size: 22px
      font-weight: 700
      letter-spacing: 0.01em
      margin: 0

    &__cat-count
      font-family: $mono
      font-size: 12px
      font-weight: 700
      color: $blue-dark
      background: rgba(40, 122, 237, 0.10)
      border-radius: 999px
      padding: 3px 10px
      min-width: 26px
      text-align: center

    &__cat-chevron
      color: $muted
      font-size: 16px
      transition: transform .22s ease, color .16s ease
      &--open
        transform: rotate(180deg)
        color: $blue

    &__cat-empty
      color: $muted
      font-size: 14px
      padding: 4px 2px 12px

    // ---------- GRID DE ROBÔS ----------
    &__grid
      display: grid
      grid-template-columns: repeat(auto-fill, minmax(210px, 1fr))
      gap: 18px
      margin-bottom: 4px

    &__card
      display: flex
      flex-direction: column
      background: $paper
      border: 1px solid $line
      border-radius: 16px
      overflow: hidden
      box-shadow: 0 2px 8px rgba(11, 14, 20, 0.05)
      transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease
      &--clickable
        cursor: pointer
      &--clickable:hover
        transform: translateY(-3px)
        border-color: rgba(40, 122, 237, 0.5)
        box-shadow: 0 12px 26px rgba(11, 14, 20, 0.14)

    &__card-media
      position: relative
      height: 158px
      display: flex
      align-items: center
      justify-content: center
      // sem padding: a foto ocupa a área inteira (ver __card-img). O ícone de
      // placeholder e o pill de status se posicionam sozinhos.
      overflow: hidden
      background: linear-gradient(160deg, #F6F8FC 0%, #E8EDF5 100%)
      border-bottom: 1px solid $line

    // Preenche todo o espaço SEM distorcer: `cover` mantém a proporção e recorta
    // o excesso, ao contrário de esticar (que deformaria o robô). Antes era
    // `contain`, que preservava a foto inteira mas deixava faixas vazias em volta
    // — foto de celular é 4:3 ou 3:4 e o card é mais largo que alto.
    &__card-img
      width: 100%
      height: 100%
      object-fit: cover
      object-position: center
      display: block

    &__card-ph
      font-size: 54px
      color: #C3CBD8

    &__status
      position: absolute
      left: 10px
      bottom: 10px
      display: inline-flex
      align-items: center
      gap: 6px
      font-family: $mono
      font-size: 10px
      font-weight: 700
      text-transform: uppercase
      letter-spacing: 0.04em
      padding: 4px 10px
      border-radius: 999px
      border: 1px solid
      backdrop-filter: blur(2px)

    &__status-dot
      width: 7px
      height: 7px
      border-radius: 50%
      flex-shrink: 0

    &__card-body
      display: flex
      flex-direction: column
      gap: 8px
      padding: 14px 16px 16px

    &__card-name
      font-family: $display
      font-size: 19px
      font-weight: 700
      line-height: 1.15
      margin: 0
      word-break: break-word

    &__card-team
      display: flex
      align-items: center
      gap: 7px
      font-size: 13px
      color: $muted
      margin: 0
      &-ic
        font-size: 12px
        color: #AEB7C4

    // ---------- INSPEÇÃO (inspetor de segurança) ----------
    &__insp
      display: flex
      flex-wrap: wrap
      gap: 8px
      margin-top: 2px

    &__insp-chip
      display: inline-flex
      align-items: center
      gap: 6px
      font-size: 11px
      font-weight: 600
      padding: 4px 10px
      border-radius: 8px
      border: 1px solid
      &.is-ok
        color: $green
        border-color: rgba(46, 158, 79, 0.4)
        background: rgba(46, 158, 79, 0.10)
      &.is-no
        color: $red
        border-color: rgba(221, 52, 52, 0.4)
        background: rgba(221, 52, 52, 0.08)

    &__insp-btn
      display: inline-flex
      align-items: center
      justify-content: center
      gap: 8px
      margin-top: 6px
      padding: 9px 12px
      border-radius: 10px
      background: linear-gradient(140deg, $blue, $blue-dark)
      color: #fff
      font-family: $display
      font-size: 14px
      font-weight: 700
      letter-spacing: 0.02em
      box-shadow: 0 4px 12px rgba(40, 122, 237, 0.28)
      transition: filter .16s ease
    &__card--clickable:hover &__insp-btn
      filter: brightness(1.06)

    // ---------- ESTADO VAZIO ----------
    &__empty
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      text-align: center
      padding: 72px 24px
      background: $paper
      border: 1px dashed rgba(11, 14, 20, 0.16)
      border-radius: 16px

    &__empty-ic
      font-size: 46px
      margin-bottom: 12px

    &__empty-title
      font-family: $display
      font-size: 22px
      font-weight: 700
      margin: 0 0 6px

    &__empty-text
      font-size: 14px
      color: $muted
      margin: 0

  @keyframes robots-rise
    from
      opacity: 0
      transform: translateY(14px)
    to
      opacity: 1
      transform: translateY(0)
</style>
